// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/USER_MANAGEMENT_UI_PLAN_COMPLETE.md
/**
 * User Filters Component
 * 
 * Provides filtering and search capabilities for users:
 * - Text search (username, email, full name)
 * - Role filter
 * - Status filter (active/inactive/all)
 * - Company filter
 * - Department filter
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';

// Import service for role/company/department options
import userManagementService from '../../services/userManagementService';

const UserFilters = ({ filters, onFiltersChange }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [roleOptions, setRoleOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      setLoading(true);
      
      // Load roles
      const rolesResponse = await userManagementService.permission.getRoles();
      setRoleOptions(Array.isArray(rolesResponse) ? rolesResponse : []);
      
      // Knowledge management removed - companies/departments not available
      setCompanyOptions([]);
      setDepartmentOptions([]);
      
    } catch (err) {
      console.error('Failed to load filter options:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    
    // Debounce search input
    if (field === 'search') {
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        onFiltersChange(newFilters);
      }, 500);
    } else {
      onFiltersChange(newFilters);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      role_id: '',
      status: 'active',
      company_id: '',
      department_id: ''
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.role_id) count++;
    if (localFilters.status !== 'active') count++;
    if (localFilters.company_id) count++;
    if (localFilters.department_id) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        {/* Search Input */}
        <Box sx={{ flex: '0 0 220px' }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Search users..."
            value={localFilters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                minHeight: '56px'
              }
            }}
          />
        </Box>

        {/* Role Filter */}
        <Box sx={{ flex: '0 0 140px' }}>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={localFilters.role_id || ''}
              label="Role"
              onChange={(e) => handleFilterChange('role_id', e.target.value)}
              sx={{ 
                borderRadius: 2,
                height: '56px',
                '& .MuiSelect-select': {
                  padding: '16.5px 14px'
                }
              }}
            >
              <MenuItem value="">
                <em>All Roles</em>
              </MenuItem>
              {roleOptions && roleOptions.length > 0 && roleOptions.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Status Filter */}
        <Box sx={{ flex: '0 0 140px' }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={localFilters.status || 'active'}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
              sx={{ 
                borderRadius: 2,
                height: '56px',
                '& .MuiSelect-select': {
                  padding: '16.5px 14px'
                }
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Company/Department Filters - REMOVED (Knowledge module not in use) */}
      </Box>

      {/* Filter Actions */}
      {(activeFiltersCount > 0 || loading) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          {/* Active Filters Indicator */}
          {activeFiltersCount > 0 && (
            <Chip
              icon={<FilterListIcon />}
              label={`${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} active`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}

          {/* Clear Filters Button */}
          {activeFiltersCount > 0 && (
            <Tooltip title="Clear all filters">
              <IconButton
                size="small"
                onClick={handleClearFilters}
                sx={{ color: 'text.secondary' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Loading Indicator */}
          {loading && (
            <Chip
              label="Loading options..."
              size="small"
              sx={{ color: 'text.secondary' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default UserFilters;
